import { BusinessUnitsTableResult } from 'src/apps/dashboard/services/queries/business-units-table/helpers/transformConsolidatedResults';
import { DashboardDataResult } from 'src/apps/dashboard/services/queries/dashboard/helpers/transformResults';
import { ScoreDataResult } from 'src/apps/dashboard/services/queries/score/helpers/transformResults';
import { BusinessUnit } from 'src/apps/dashboard/types/DashboardTypes';
import {
	IGetMfaSessionsParams,
	IGetMobileIssuesParams,
	IGetMobileSdkTestsParams,
	IMobileAllIssuesResult,
	IMobileUniqueIssuesResult,
	TMfaSessionsResult,
	TMobileSdkTestsResult
} from 'src/apps/dashboard/types/MobileDashboardServiceTypes';
import { IFilterOptions } from 'src/common/helpers/DataFilterHelper';
import { gqlClient } from 'src/common/services/api/GraphQLRequestApi';
import { Logger } from 'src/common/services/Logger';
import { LabelsExcludedFromDashboard } from 'src/common/types/ConfigurationTypes';

import { buildMfaSessionsVariables } from './queries/mobile/helpers/buildMfaSessionsVariables';
import { buildMobileCoreLabelIssuesVariables } from './queries/mobile/helpers/buildMobileCoreLabelIssuesVariables';
import { buildMobileSdkTestsVariables } from './queries/mobile/helpers/buildMobileSdkTestsVariables';
import { buildMobileUniqueIssuesVariables } from './queries/mobile/helpers/buildMobileUniqueIssuesVariables';
import { transformMfaSessionsResults } from './queries/mobile/helpers/transformMfaSessionsResults';
import { transformMobileAllIssuesResults } from './queries/mobile/helpers/transformMobileAllIssuesResults';
import {
	IMobileAppIdFilterOptions,
	transformMobileAppIdFilterOptionsResults
} from './queries/mobile/helpers/transformMobileAppIdFilterOptionsResults';
import {
	buildMobileBusinessUnitsTableVariables,
	transformMobileBusinessUnitsTableResults
} from './queries/mobile/helpers/transformMobileBusinessUnitsTableResults';
import {
	buildMobileDashboardDataVariables,
	transformMobileDashboardDataResults
} from './queries/mobile/helpers/transformMobileDashboardDataResults';
import {
	buildMobileScoreDataVariables,
	transformMobileScoreDataResults
} from './queries/mobile/helpers/transformMobileScoreDataResults';
import { transformMobileSdkTestsResults } from './queries/mobile/helpers/transformMobileSdkTestsResults';
import { transformMobileUniqueIssuesResults } from './queries/mobile/helpers/transformMobileUniqueIssuesResults';
import { MobileDashboardMfaSessionsQuery } from './queries/mobile/MfaSessions.generated';
import MobileDashboardMfaSessions from './queries/mobile/MfaSessions.gql';
import { MobileAppIdFilterOptionsQuery } from './queries/mobile/MobileAppIdFilterOptions.generated';
import MobileAppIdFilterOptions from './queries/mobile/MobileAppIdFilterOptions.gql';
import { MobileBusinessUnitsTableDataQuery } from './queries/mobile/MobileBusinessUnitsTable.generated';
import MobileBusinessUnitsTableData from './queries/mobile/MobileBusinessUnitsTable.gql';
import { MobileCoreLabelAllIssuesQuery } from './queries/mobile/MobileCoreLabelIssues.generated';
import MobileCoreLabelAllIssues from './queries/mobile/MobileCoreLabelIssues.gql';
import { MobileDashboardDataQuery } from './queries/mobile/MobileDashboardData.generated';
import MobileDashboardData from './queries/mobile/MobileDashboardData.gql';
import { MobileScoreDataQuery } from './queries/mobile/MobileScoreData.generated';
import MobileScoreData from './queries/mobile/MobileScoreData.gql';
import { MobileDashboardMobileSdkTestsQuery } from './queries/mobile/MobileSdkTests.generated';
import MobileDashboardMobileSdkTests from './queries/mobile/MobileSdkTests.gql';
import { MobileDashboardUniqueIssuesQuery } from './queries/mobile/MobileUniqueIssues.generated';
import MobileDashboardUniqueIssues from './queries/mobile/MobileUniqueIssues.gql';

export const getDashboardData = async (
	days: number,
	filters?: IFilterOptions,
	labelsExcludedFromDashboard?: LabelsExcludedFromDashboard,
	mobileBuIds?: string[]
): Promise<DashboardDataResult> => {
	try {
		const variables = buildMobileDashboardDataVariables(
			days,
			filters,
			labelsExcludedFromDashboard,
			mobileBuIds
		);
		const result = await gqlClient.request<MobileDashboardDataQuery>(
			MobileDashboardData,
			variables
		);
		return transformMobileDashboardDataResults(result);
	} catch (error) {
		Logger.error('MobileDashboardService -> getDashboardData failed with an error:', error);
		throw error;
	}
};

export const getScoreData = async (
	filters: IFilterOptions | undefined,
	selectedPeriod: number,
	mobileBuIds?: string[]
): Promise<ScoreDataResult> => {
	try {
		const variables = buildMobileScoreDataVariables(selectedPeriod, filters, mobileBuIds);
		const result = await gqlClient.request<MobileScoreDataQuery>(MobileScoreData, variables);
		return transformMobileScoreDataResults(result);
	} catch (error) {
		Logger.error('MobileDashboardService -> getScoreData failed with an error:', error);
		throw error;
	}
};

export const getBusinessUnitsTableData = async ({
	days,
	businessUnits,
	filters
}: {
	days: number;
	businessUnits: BusinessUnit[];
	filters?: IFilterOptions;
}): Promise<BusinessUnitsTableResult> => {
	try {
		const variables = buildMobileBusinessUnitsTableVariables(days, filters);
		const result = await gqlClient.request<MobileBusinessUnitsTableDataQuery>(
			MobileBusinessUnitsTableData,
			variables
		);
		return transformMobileBusinessUnitsTableResults(result, businessUnits);
	} catch (error) {
		Logger.error(
			'MobileDashboardService -> getBusinessUnitsTableData failed with an error:',
			error
		);
		throw error;
	}
};

export const getMfaSessions = async (
	params: IGetMfaSessionsParams
): Promise<TMfaSessionsResult> => {
	try {
		const variables = buildMfaSessionsVariables(params);
		const result = await gqlClient.request<MobileDashboardMfaSessionsQuery>(
			MobileDashboardMfaSessions,
			variables
		);
		return transformMfaSessionsResults(result);
	} catch (error) {
		Logger.error('MobileDashboardService -> getMfaSessions failed with an error:', error);
		throw error;
	}
};

export const getMobileSdkTests = async (
	params: IGetMobileSdkTestsParams
): Promise<TMobileSdkTestsResult> => {
	try {
		const variables = buildMobileSdkTestsVariables(params);
		const result = await gqlClient.request<MobileDashboardMobileSdkTestsQuery>(
			MobileDashboardMobileSdkTests,
			variables
		);
		return transformMobileSdkTestsResults(result);
	} catch (error) {
		Logger.error('MobileDashboardService -> getMobileSdkTests failed with an error:', error);
		throw error;
	}
};

export const getUniqueIssues = async ({
	filters,
	page,
	sizePerPage,
	sort,
	mobileBuIds
}: IGetMobileIssuesParams): Promise<IMobileUniqueIssuesResult> => {
	try {
		const variables = buildMobileUniqueIssuesVariables(
			filters,
			page,
			sizePerPage,
			sort,
			mobileBuIds
		);
		const result = await gqlClient.request<MobileDashboardUniqueIssuesQuery>(
			MobileDashboardUniqueIssues,
			variables
		);
		return transformMobileUniqueIssuesResults(result);
	} catch (error) {
		Logger.error('MobileDashboardService -> getUniqueIssues failed with an error:', error);
		throw error;
	}
};

export const getAllIssues = async ({
	filters,
	page,
	sizePerPage,
	sort,
	mobileBuIds
}: IGetMobileIssuesParams): Promise<IMobileAllIssuesResult> => {
	try {
		const variables = buildMobileCoreLabelIssuesVariables(
			filters,
			page,
			sizePerPage,
			sort,
			mobileBuIds
		);
		const result = await gqlClient.request<MobileCoreLabelAllIssuesQuery>(
			MobileCoreLabelAllIssues,
			variables
		);
		return transformMobileAllIssuesResults(result);
	} catch (error) {
		Logger.error('MobileDashboardService -> getAllIssues failed with an error:', error);
		throw error;
	}
};

export const getMobileAppIdFilterOptions = async (): Promise<IMobileAppIdFilterOptions> => {
	try {
		const result = await gqlClient.request<MobileAppIdFilterOptionsQuery>(MobileAppIdFilterOptions);
		return transformMobileAppIdFilterOptionsResults(result);
	} catch (error) {
		Logger.error(
			'MobileDashboardService -> getMobileAppIdFilterOptions failed with an error:',
			error
		);
		throw error;
	}
};
